import { EditOutlined, UnorderedListOutlined } from "@ant-design/icons"
import { AutoComplete, Button, Form } from "antd"
import { type FormInstance } from "antd/es/form/hooks/useForm"
import { type BaseOptionType } from "antd/lib/select"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { getEntityName } from "../../../../../../../utils/user/get-entity-name"
import { useProjectOutletContext } from "../../../../../hooks/use-project-outlet-context.tsx"
import { RESPONSIBLE_TYPES } from "../../../../constants/responsible-type-constants.ts"
import { type FinancialTransactionItem } from "../../../../../types/project-finance.types.tsx"
import { UNRESOLVED_USER_LABEL } from "../../../../../../../utils/select/entity-labels.ts"
import { ResolvedSelect } from "../../../../../../../components/Select/ResolvedSelect.tsx"
import { buildStakeholderOptions, normalize } from "./buildStakeholderOptions.ts"
import { SubcontractorAutoCompleteWithCreate } from "./SubcontractorAutoCompleteWithCreate.tsx"
import { SupplierAutoCompleteWithCreate } from "./SupplierAutoCompleteWithCreate.tsx"
import { TextInput } from "../../../../../../../components/Input/TextInput.tsx"


const values = RESPONSIBLE_TYPES

type StakeholderEntity = "subcontractor" | "assignee" | "name"

/**
 * The EXPENSE picker combines FK-keyed entities (subcontractors, team members)
 * and name-keyed parties (suppliers, customer) in one Select. Each option carries
 * an `entity` discriminator so the picked value is routed to the right form field
 * — the FK id to `subcontractor`/`assignee`, or the free-text name to
 * `custom_subcontractor_name` — and the others are cleared. Exported so the
 * routing can be asserted directly (the antd dropdown is flaky to drive in jsdom).
 */
export function resolveStakeholderPatch(
  entity: StakeholderEntity | undefined,
  value: string | null | undefined
) {
  return {
    subcontractor: entity === "subcontractor" ? (value ?? null) : null,
    assignee: entity === "assignee" ? (value ?? null) : null,
    custom_subcontractor_name: entity === "name" ? (value ?? null) : null
  }
}

// The EXPENSE picker mixes subcontractor/team-member ids and supplier/customer
// names in one Select. Those keys come from different id-spaces, so the Select
// option value is keyed by entity (`entity:key`) — otherwise a subcontractor id
// equal to a team-member id would highlight the wrong option. Routing still goes
// through the entity, not the raw key.
export function makeStakeholderValue(
  entity: StakeholderEntity,
  id: string | number
) {
  return `${entity}:${id}`
}

export function parseStakeholderValue(composite?: string | null): {
  entity: StakeholderEntity | undefined
  id: string | null
} {
  if (!composite) return { entity: undefined, id: null }
  const sep = composite.indexOf(":")
  if (sep < 0) return { entity: undefined, id: null }
  return {
    entity: composite.slice(0, sep) as StakeholderEntity,
    id: composite.slice(sep + 1)
  }
}

/**
 * The EXPENSE picker's grouped options: FK-keyed subcontractors and team members
 * (so an existing entity link is preserved) plus name-keyed suppliers and the
 * project customer. Empty supplier / customer groups are omitted. Exported for a
 * direct unit test (the antd grouped dropdown is flaky to drive in jsdom).
 */
export function buildExpenseStakeholderGroups(
  subcontractors: BaseOptionType[] | undefined,
  teamMembers: BaseOptionType[] | undefined,
  suppliers: BaseOptionType[] | undefined,
  customerName: string | null | undefined
): BaseOptionType[] {
  const groups: BaseOptionType[] = [
    {
      label: "Subcontractors",
      options: (subcontractors ?? []).map((option) => ({
        label: option.label,
        value: makeStakeholderValue("subcontractor", option.value)
      }))
    },
    {
      label: "Team members",
      options: (teamMembers ?? []).map((option) => ({
        label: option.label,
        value: makeStakeholderValue("assignee", option.value)
      }))
    }
  ]

  const supplierNames = (suppliers ?? [])
    .map((option) => String(option.label ?? ""))
    .filter(Boolean)
  if (supplierNames.length) {
    groups.push({
      label: "Suppliers",
      options: supplierNames.map((name) => ({
        label: name,
        value: makeStakeholderValue("name", name)
      }))
    })
  }

  const customer = (customerName ?? "").trim()
  if (customer) {
    groups.push({
      label: "Customer",
      options: [
        { label: customer, value: makeStakeholderValue("name", customer) }
      ]
    })
  }

  return groups
}

/**
 * The EXPENSE picker is a searchable select that stays editable for one-off
 * parties (FR-12): a typed name matching no existing stakeholder is offered as a
 * name-keyed option, and the current one-off selection is kept selectable so its
 * label renders instead of the raw composite value. Both route to
 * `custom_subcontractor_name`. Exported for a direct unit test (the antd dropdown
 * is flaky to drive in jsdom).
 */
export function buildExpenseStakeholderOptions(
  subcontractors: BaseOptionType[] | undefined,
  teamMembers: BaseOptionType[] | undefined,
  suppliers: BaseOptionType[] | undefined,
  customerName: string | null | undefined,
  typed: string,
  selectedName: string | null | undefined
): BaseOptionType[] {
  const groups = buildExpenseStakeholderGroups(
    subcontractors,
    teamMembers,
    suppliers,
    customerName
  )
  const labelInGroups = (name: string) =>
    groups.some((group) =>
      (group.options ?? []).some(
        (option: BaseOptionType) =>
          normalize(String(option.label ?? "")) === normalize(name)
      )
    )
  const valueInGroups = (value: string) =>
    groups.some((group) =>
      (group.options ?? []).some(
        (option: BaseOptionType) => option.value === value
      )
    )

  const oneOffs: BaseOptionType[] = []

  // Keep the current selection renderable. Its controlled value is name-keyed,
  // which never collides with the FK-keyed subcontractor/team-member values, so
  // dedupe by exact value (and keep the name untrimmed to match the form value)
  // — a custom name equal to a stakeholder *label* must still get its own option.
  const selected = selectedName ?? ""
  if (selected) {
    const selectedValue = makeStakeholderValue("name", selected)
    if (!valueInGroups(selectedValue)) {
      oneOffs.push({ label: selected, value: selectedValue })
    }
  }

  const typedName = typed.trim()
  if (
    typedName &&
    !labelInGroups(typedName) &&
    normalize(typedName) !== normalize(selected)
  ) {
    oneOffs.push({ label: typedName, value: makeStakeholderValue("name", typedName) })
  }

  return oneOffs.length ? [...groups, ...oneOffs] : groups
}

interface ProjectViewFinancePersonSelectProps {
  form: FormInstance<FinancialTransactionItem>
  subcontractors: BaseOptionType[]
  // Project stakeholders only (assigned or referenced on finance lines) —
  // feeds the all-stakeholder party pickers, while the contractor picker
  // keeps the full company list.
  projectSubcontractors?: BaseOptionType[]
  teamMembers: BaseOptionType[]
  suppliers?: BaseOptionType[]
  hasInitialValue: boolean
}

export function ProjectViewFinancePersonSelect({
  form,
  subcontractors,
  projectSubcontractors,
  teamMembers,
  suppliers,
  hasInitialValue
}: ProjectViewFinancePersonSelectProps) {
  const { t } = useTranslation()
  const { project } = useProjectOutletContext()
  const customer = project?.customer || project?.temporary_customer
  const customerFullName = getEntityName(customer, "")

  const responsibleType = Form.useWatch("responsible_type", form)
  const subcontractorValue = Form.useWatch("subcontractor", form)
  const assigneeValue = Form.useWatch("assignee", form)
  const customNameValue = Form.useWatch("custom_subcontractor_name", form)

  const isCustomName = !!form.getFieldValue("custom_subcontractor_name")
  const [useCustomName, setUseCustomName] = useState(isCustomName)
  const isInitialMount = useRef(true)

  const [partySearch, setPartySearch] = useState("")

  const isRequired = !!responsibleType

  useEffect(() => {
    if (responsibleType === undefined || responsibleType === null) return

    if (hasInitialValue && isInitialMount.current) {
      isInitialMount.current = false
      return
    }

    // Clear all person-related fields
    form.setFieldsValue({
      subcontractor: null,
      assignee: null,
      custom_subcontractor_name: null
    })
    setPartySearch("")

    if (
      responsibleType === values.CONTRACTOR ||
      responsibleType === values.TEAM_MEMBER ||
      responsibleType === values.EXPENSE
    ) {
      setUseCustomName(false)
    }

    if (responsibleType === values.CUSTOMER) {
      form.setFieldValue("custom_subcontractor_name", customerFullName)
    }
  }, [responsibleType, form, customerFullName])

  // Shared custom-name input + "switch back to list" toggle, reused by the
  // single-field picker (contractor / team member) and the EXPENSE picker.
  const renderCustomNameInput = () => (
    <>
      <div className="min-w-0 flex-1">
        <Form.Item<FinancialTransactionItem>
          name="custom_subcontractor_name"
          noStyle
          rules={[{ required: isRequired, message: t("finance.personSelect.fieldRequired") }]}
        >
          <TextInput placeholder="Enter name" />
        </Form.Item>
      </div>
      <Button
        className="shrink-0"
        icon={<UnorderedListOutlined />}
        onClick={() => {
          setUseCustomName(false)
          form.setFieldValue("custom_subcontractor_name", null)
        }}
        title="Select from list"
      />
    </>
  )

  const renderSelectWithCustomToggle = (
    fieldName: "subcontractor" | "assignee",
    options: BaseOptionType[],
    placeholder: string
  ) => (
    <div className="flex gap-1">
      {useCustomName ? (
        renderCustomNameInput()
      ) : (
        <>
          <div className="min-w-0 flex-1">
            <Form.Item<FinancialTransactionItem>
              name={fieldName}
              noStyle
              rules={[
                { required: isRequired, message: t("finance.personSelect.fieldRequired") }
              ]}
            >
              {fieldName === "subcontractor" ? (
                <SubcontractorAutoCompleteWithCreate
                  subcontractors={options}
                  placeholder={placeholder}
                />
              ) : (
                <ResolvedSelect
                  className="w-full"
                  placeholder={placeholder}
                  options={options}
                  fallbackLabel={UNRESOLVED_USER_LABEL}
                  showSearch
                  allowClear
                  filterOption={(input, option) =>
                    normalize(option?.label ?? "").includes(normalize(input))
                  }
                />
              )}
            </Form.Item>
          </div>
          <Button
            className="shrink-0"
            icon={<EditOutlined />}
            onClick={() => {
              setUseCustomName(true)
              form.setFieldValue(fieldName, null)
            }}
            title="Enter custom name"
          />
        </>
      )}
    </div>
  )

  // CONTRACTOR - subcontractor select or custom input
  if (responsibleType === values.CONTRACTOR) {
    return renderSelectWithCustomToggle(
      "subcontractor",
      subcontractors,
      "Select contractor"
    )
  }

  // TEAM_MEMBER - assignee select or custom input
  if (responsibleType === values.TEAM_MEMBER) {
    return renderSelectWithCustomToggle(
      "assignee",
      teamMembers,
      "Select team member"
    )
  }

  // EXPENSE - a searchable all-stakeholder select that stays editable for
  // one-offs: existing subcontractors / team members route to their FK, while a
  // typed party not in the lists is recorded as a free-text name (FR-12).
  if (responsibleType === values.EXPENSE) {
    const stakeholderOptions = buildExpenseStakeholderOptions(
      projectSubcontractors ?? subcontractors,
      teamMembers,
      suppliers,
      customerFullName,
      partySearch,
      customNameValue
    )

    const selectedStakeholderValue = subcontractorValue
      ? makeStakeholderValue("subcontractor", subcontractorValue)
      : assigneeValue
        ? makeStakeholderValue("assignee", assigneeValue)
        : customNameValue
          ? makeStakeholderValue("name", customNameValue)
          : undefined

    return (
      <ResolvedSelect
        className="w-full"
        placeholder="Select stakeholder"
        options={stakeholderOptions}
        fallbackLabel={UNRESOLVED_USER_LABEL}
        value={selectedStakeholderValue}
        showSearch
        allowClear
        onSearch={setPartySearch}
        filterOption={(input, option) =>
          normalize(option?.label ?? "").includes(normalize(input))
        }
        onChange={(value) => {
          const { entity, id } = parseStakeholderValue(value)
          form.setFieldsValue(resolveStakeholderPatch(entity, id))
        }}
      />
    )
  }

  // CUSTOMER - free-text, prefilled with the customer name (unchanged)
  if (responsibleType === values.CUSTOMER) {
    return (
      <Form.Item<FinancialTransactionItem>
        name="custom_subcontractor_name"
        noStyle
        rules={[{ required: isRequired, message: t("finance.personSelect.fieldRequired") }]}
      >
        <TextInput placeholder="Enter name" />
      </Form.Item>
    )
  }

  // SUPPLIER routes to SupplierAutoCompleteWithCreate (it can create a
  // missing supplier via the real form); this fallthrough now only serves
  // MISCELLANEOUS / REIMBURSEMENT, free-text name pickers with no create
  // affordance.
  if (responsibleType === values.SUPPLIER) {
    return (
      <Form.Item<FinancialTransactionItem>
        name="custom_subcontractor_name"
        noStyle
        rules={[{ required: isRequired, message: t("finance.personSelect.fieldRequired") }]}
      >
        <SupplierAutoCompleteWithCreate suppliers={suppliers} />
      </Form.Item>
    )
  }

  const customerOptions = customerFullName
    ? [{ label: customerFullName, value: customerFullName }]
    : undefined
  const stakeholderOptions = buildStakeholderOptions(
    suppliers,
    projectSubcontractors ?? subcontractors,
    teamMembers,
    customerOptions
  )

  return (
    <Form.Item<FinancialTransactionItem>
      name="custom_subcontractor_name"
      noStyle
      rules={[{ required: isRequired, message: t("finance.personSelect.fieldRequired") }]}
    >
      <AutoComplete
        className="w-full"
        placeholder={t("suppliers.party.placeholder")}
        options={stakeholderOptions}
        allowClear
        onSearch={setPartySearch}
        filterOption={(input, option) =>
          normalize(String(option?.value ?? "")).includes(normalize(input))
        }
      />
    </Form.Item>
  )
}
